import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loading } from "../../components/Loading";
import { QueueHeader } from "../../components/QueueHeader";
import { ConfigType } from "../../contexts/AppConfigContext";
import { useAppConfig } from "../../hooks/useAppConfig";
import { usePluginspace } from "../../hooks/usePluginspace";
import "./index.css";
import Swal from "sweetalert2";
import api from "../../services/api";
import InputMask from "react-input-mask";
import { telephoneNoMask } from "../../functions/mask";
import { Clock, Users, User, Phone, Search } from 'lucide-react';
import { ModalTerms } from "../../components/ModalTerms";
import { useToast } from "../../hooks/useToast";
import { useFoodConfig } from "../../hooks/useFood";

// Importar fonte Poppins
if (typeof document !== 'undefined') {
  const link = document.createElement('link');
  link.href = 'https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap';
  link.rel = 'stylesheet';
  if (!document.head.querySelector('link[href*="Poppins"]')) {
    document.head.appendChild(link);
  }
  
  // Configurar viewport para mobile responsivo
  let viewport = document.querySelector('meta[name="viewport"]');
  if (!viewport) {
    viewport = document.createElement('meta');
    viewport.setAttribute('name', 'viewport');
    document.head.appendChild(viewport);
  }
  viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
}

type ImageType = {
  id: string;
  url: string;
};

type QueueConfigType = {
  id: string;
  able: boolean;
  limitPeople: number;
  limitPeopleGroup: number;
  limitTime: number;
  images: ImageType[];
  useTerms: boolean;
  useMenu: boolean;
  useLocation: boolean;
  useNinegridMenu: boolean;
  externalMenuLink: string;
  limitQueue: number;
};

export function Queue() {
  const { pluginspace_id } = useParams();
  const { registerAccess, getToken, getVerifyShift, getThisPluginspace } =
    usePluginspace();
  const navigate = useNavigate();

  const { getAppConfig } = useAppConfig();
  const [config, setConfig] = useState<ConfigType | void>();
  const [loading, setLoading] = useState(true);
  const [loaderLocation, setLoaderLocation] = useState(false);
  const [queueConfig, setQueueConfig] = useState<QueueConfigType | null>(null);
  const [people, setPeople] = useState(0);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneFind, setPhoneFind] = useState("");
  const [maskTelephone, setMaskTelephone] = useState("(99) 9999-9999");
  const [maskTelephoneFind, setMaskTelephoneFind] = useState("(99) 9999-9999");
  const [acceptTerm, setAcceptTerm] = useState(false);
  const [term, setTerm] = useState("");
  const [openTerms, setOpenTerms] = useState(false);
  const [validShift, setValidShift] = useState(false);
  const [preferentialQueue, setPreferentialQueue] = useState("n");
  const [locationShop, setLocationShop] = useState<any>();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const toastComponent = useToast();

  const useFood = useFoodConfig();

  useEffect(() => {
    registerAccess("queue");
    init();
  }, []);

  // 🎠 CARROSEL: Rotaciona entre as imagens a cada 5 segundos
  useEffect(() => {
    if (queueConfig && queueConfig.images && queueConfig.images.length > 0) {
      const interval = setInterval(() => {
        setCurrentImageIndex((prevIndex) =>
          (prevIndex + 1) % queueConfig.images.length
        );
      }, 5000); // Muda a imagem a cada 5 segundos
      return () => clearInterval(interval);
    }
  }, [queueConfig]);

  async function init() {
    setLoading(true);
    toastComponent.setToastPosition("0rem");
    setValidShift(await getVerifyShift());
    await getConfig();
    await getQueueConfig();
    await getTerms();
    await updateLocationShop();
    setLoading(false);
  }

  const updateLocationShop = async () => {
    const res: any = await getThisPluginspace();
    setLocationShop(res);
  };

  async function getTerms() {
    await api
      .post(
        `/api/consent/filter`,
        {
          active: true,
        },
        {
          headers: {
            Authorization: `Bearer ${await getToken()}`,
          },
        }
      )
      .then(async (response) => {
        if (response.data.data && response.data.data[0]) {
          setTerm(response.data.data[0].content);
        } else {
          setTerm("");
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }

  async function getQueueConfig() {
    await api
      .get("/api/queueReservation/queueConfig", {
        headers: {
          Authorization: `Bearer ${await getToken()}`,
        },
      })
      .then((response) => {
        const queueResponse = response.data;
        if (queueResponse && queueResponse.data && queueResponse.data[0]) {
          const queueConfigItem = queueResponse.data[0];
          if (queueConfigItem && queueConfigItem.able) {
            setQueueConfig(queueConfigItem);
          } else {
            Swal.fire({
              icon: "error",
              title: "Fila não está habilitada!",
              text: "Loja não está com o esquema de filas habilitada.",
            }).then(() => navigate(`/${pluginspace_id}`));
          }
        } else {
          Swal.fire({
            icon: "error",
            title: "Fila não configurada!",
            text: "Loja não está com o esquema de filas configurada.",
          }).then(() => navigate(`/${pluginspace_id}`));
        }
      })
      .catch((err) => {
        console.log(err);
        Swal.fire({
          icon: "error",
          title: "Fila não configurada!",
          text: "Loja não está com o esquema de filas configurada.",
        }).then(() => navigate(`/${pluginspace_id}`));
      });
  }
  async function habilitarGeolocalizacao() {
    try {
      if ("geolocation" in navigator) {
        // Return a Promise that resolves or rejects based on the geolocation result
        const position: any = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(
            (position) => resolve({ success: true, data: position }),
            (error) => {
              let errorMessage = "";

              switch (error.code) {
                case error.PERMISSION_DENIED:
                  errorMessage =
                    "Você bloqueou a permissão de localização. Para continuar, vá até as configurações do dispositivo e toque em “redefinir permissões”. Em seguida, tente novamente.";
                  break;
                case error.POSITION_UNAVAILABLE:
                  errorMessage = "A localização está indisponível.";
                  break;
                case error.TIMEOUT:
                  errorMessage = "Tempo limite para obter a localização.";
                  break;
                default:
                  errorMessage =
                    "Ocorreu um erro desconhecido ao obter a localização.";
                  break;
              }

              toastComponent.openToast("warning", errorMessage, 8000);
              reject({ success: false, error: error });
            }
          );
        });

        return position;
      } else {
        toastComponent.openToast(
          "warning",
          "Geolocalização não é suportada pelo seu navegador.",
          8000
        );
        return { success: false, data: {} };
      }
    } catch (error) {
      return { success: false, error: error };
    }
  }

  async function enterQueue() {
    let latCliente = 0;
    let longCliente = 0;
    let distanciaCalc = 0;

    if (!validShift) {
      toastComponent.openToast(
        "warning",
        "Loja fechada, impossível entrar na fila.",
        4000
      );
      /* Swal.fire({
                icon: 'warning',
                title: 'Atenção!',
                text: 'Loja fechada, impossível entrar na fila.',
            })*/
      return;
    }

    if (
      !acceptTerm &&
      term !== "" &&
      term &&
      queueConfig &&
      queueConfig.useTerms
    ) {
      toastComponent.openToast(
        "error",
        "É necessário aceitar os termos para continuar.",
        4000
      );
      /* Swal.fire({
                icon: 'error',
                title: 'Erro',
                text: 'É necessário aceitar os termos para continuar.',
            })*/
      return;
    }
    if (people <= 0) {
      toastComponent.openToast(
        "warning",
        "Precisa haver uma pessoa ou mais para entrar na fila.",
        4000
      );
      /*
            Swal.fire({
                icon: 'warning',
                title: 'Atenção!',
                text: `Precisa haver uma pessoa ou mais para entrar na fila.`,
            })*/
      return;
    }

    if (queueConfig && people > queueConfig.limitPeopleGroup) {
      toastComponent.openToast(
        "warning",
        `Há mais pessoas do que o permitido na fila de espera (Máximo ${queueConfig.limitPeopleGroup})`,
        4000
      );
      /*Swal.fire({
                icon: 'warning',
                title: 'Atenção!',
                html: `Há mais pessoas do que o permitido na fila de espera (Máximo ${queueConfig.limitPeopleGroup})`,
            })*/
      return;
    }

    if (!name) {
      toastComponent.openToast(
        "warning",
        `É necessário adicionar um nome para entrar na fila.`,
        4000
      );
      /*Swal.fire({
                icon: 'warning',
                title: 'Atenção!',
                text: `É necessário adicionar um nome para entrar na fila.`,
            })*/
      return;
    }

    if (!name.split(" ")[1]) {
      toastComponent.openToast(
        "warning",
        `É necessário adicionar um sobrenome para entrar na fila.`,
        4000
      );
      /* Swal.fire({
                icon: 'warning',
                title: 'Atenção!',
                text: `É necessário adicionar um sobrenome para entrar na fila.`,
            })*/
      return;
    }

    if (!phone || phone.includes("_")) {
      toastComponent.openToast("warning", `Celular inválido.`, 4000);
      /* Swal.fire({
                icon: 'warning',
                title: 'Atenção!',
                text: `Celular inválido.`,
            })*/
      return;
    }

    const subscriptionStorage = localStorage.getItem(
      window.btoa("subscription_notification")
    );
    let subscription = null;
    if (
      subscriptionStorage &&
      window.atob(subscriptionStorage) &&
      JSON.parse(window.atob(subscriptionStorage))
    ) {
      subscription = JSON.parse(window.atob(subscriptionStorage));
    }

    setLoading(true);

    if (queueConfig && queueConfig.useLocation) {
      setLoaderLocation(true);

      const habilitadoLocalizacao = await habilitarGeolocalizacao();

      latCliente = habilitadoLocalizacao.data.coords.latitude;
      longCliente = habilitadoLocalizacao.data.coords.longitude;

      const distancia = useFood.calculateDistance(
        parseFloat(locationShop.address.lat),
        parseFloat(locationShop.address.lng),
        parseFloat(habilitadoLocalizacao.data.coords.latitude),
        parseFloat(habilitadoLocalizacao.data.coords.longitude)
      );

      distanciaCalc = distancia;

      const res = distancia * 1000 <= queueConfig.limitQueue;
      setLoaderLocation(false);

      if (!res) {
        toastComponent.openToast(
          "error",
          `É necessário estar próximo ao estabelecimento para entrar na fila.`,
          4000
        );

        setLoading(false);
        return null;
      }

      if (!habilitadoLocalizacao || !habilitadoLocalizacao.success) {
        setLoading(false);
        return null;
      }
    }

    await api
      .post(
        "/api/queueReservation/queue",
        {
          people: parseInt(people.toString()),
          name,
          phone: telephoneNoMask(phone),
          preferential: preferentialQueue === "y" ? true : false,
          subscription,
          urlNotification: window.location.href + "/" + telephoneNoMask(phone),
        },
        {
          headers: {
            Authorization: `Bearer ${await getToken()}`,
          },
        }
      )
      .then((response) => {
        setLoading(false);
        const queueResponse = response.data;
        if (queueResponse.success) {
          navigate(`/${pluginspace_id}/queue/${telephoneNoMask(phone)}`, {
            state: {
              latLoja: locationShop.address.lat,
              longLoja: locationShop.address.lng,
              latCliente: latCliente,
              longCliente: longCliente,
              calculo: distanciaCalc * 1000,
            },
          });
        } else {
          toastComponent.openToast(
            "error",
            `Não foi possível entrar na fila.`,
            4000
          );
        }
      })
      .catch((err) => {
        setLoading(false);
        let title = "Erro ao entrar na fila!";
        let text = "Não foi possível entrar na fila.";
        if (
          err &&
          err.response &&
          err.response.data &&
          err.response.data.error
        ) {
          title = err.response.data.error;
        }
        if (
          err &&
          err.response &&
          err.response.data &&
          err.response.data.message
        ) {
          text = err.response.data.message;
        }

        toastComponent.openToast("error", text, 4000);
      });
  }

  async function getConfig() {
    setConfig(await getAppConfig());
  }

  function changePhone(newPhone: string) {
    const phoneValidated = newPhone.replaceAll("_", "");
    if (phoneValidated.length >= 14 && phoneValidated[13] !== "_") {
      setMaskTelephone("(99) 99999-9999");
    } else {
      setMaskTelephone("(99) 9999-9999");
    }
  }

  function changePhoneFind(newPhone: string) {
    const phoneValidated = newPhone.replaceAll("_", "");
    if (phoneValidated.length >= 14 && phoneValidated[13] !== "_") {
      setMaskTelephoneFind("(99) 99999-9999");
    } else {
      setMaskTelephoneFind("(99) 9999-9999");
    }
  }

  return (
    <>
      <QueueHeader />
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
        padding: '40px 32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflowX: 'hidden',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        {loading ? (
          <div style={{
            position: 'fixed',
            top: '80px',
            left: 0,
            right: 0,
            height: 'calc(100vh - 80px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(248, 249, 250, 0.95)',
            zIndex: 999
          }}>
            <Loading type="spinningBubbles" color="#FFF" width={64} height={64} />
          </div>
        ) : (
          <div style={{
            maxWidth: '800px',
            margin: '0 auto',
            width: '100%'
          }}>
            {!validShift && (
              <div style={{
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                color: 'white',
                padding: '16px 24px',
                borderRadius: '16px',
                textAlign: 'center',
                marginBottom: '24px',
                fontFamily: "'Poppins', sans-serif",
                fontWeight: '600',
                boxShadow: '0 4px 20px rgba(239, 68, 68, 0.3)'
              }}>
                Loja Fechada
              </div>
            )}
            {queueConfig && (
              <div className="body-queue" style={{
                width: '100%',
                maxWidth: '100%',
                margin: '0 auto',
                padding: '0',
                boxSizing: 'border-box'
              }}>
                {/* Page Header */}
                <div style={{
                    textAlign: "center",
                    marginBottom: "40px",
                    padding: "0 clamp(16px, 3vw, 32px)"
                  }}>
                    <div style={{
                      display: "flex",
                      justifyContent: "center",
                      marginBottom: "20px"
                    }}>
                      {queueConfig && queueConfig.images && queueConfig.images.length > 0 ? (
                        // 🎠 CARROSSEL: Mostrar imagens
                        <div style={{
                          position: "relative",
                          width: "clamp(300px, 50vw, 500px)",
                          aspectRatio: "16/9",
                          borderRadius: "24px",
                          overflow: "hidden",
                          boxShadow: "0 12px 32px rgba(67, 56, 202, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.4)",
                          border: "2px solid rgba(99, 102, 241, 0.2)"
                        }}>
                          {/* Imagem atual */}
                          <img
                            src={queueConfig.images[currentImageIndex].url}
                            alt={`Imagem ${currentImageIndex + 1}`}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              transition: "opacity 10s ease-in-out"
                            }}
                          />

                          {/* Overlay com gradiente */}
                          <div style={{
                            position: "absolute",
                            inset: 0,
                            background: "linear-gradient(135deg, rgba(67, 56, 202, 0.1) 0%, rgba(118, 75, 162, 0.05) 100%)",
                            pointerEvents: "none"
                          }} />

                          {/* Indicadores de página (dots) */}
                          {queueConfig.images.length > 1 && (
                            <div style={{
                              position: "absolute",
                              bottom: "12px",
                              left: "50%",
                              transform: "translateX(-50%)",
                              display: "flex",
                              gap: "6px",
                              zIndex: 10
                            }}>
                              {queueConfig.images.map((_, index) => (
                                <div
                                  key={index}
                                  onClick={() => setCurrentImageIndex(index)}
                                  style={{
                                    width: "8px",
                                    height: "8px",
                                    borderRadius: "50%",
                                    backgroundColor: index === currentImageIndex ? "rgba(255, 255, 255, 0.9)" : "rgba(255, 255, 255, 0.4)",
                                    cursor: "pointer",
                                    transition: "all 0.3s ease",
                                    boxShadow: index === currentImageIndex ? "0 2px 8px rgba(0, 0, 0, 0.3)" : "none"
                                  }}
                                />
                              ))}
                            </div>
                          )}

                          {/* Setas de navegação (se houver mais de 1 imagem) */}
                          {queueConfig.images.length > 1 && (
                            <>
                              <button
                                onClick={() =>
                                  setCurrentImageIndex(
                                    (prevIndex) =>
                                      (prevIndex - 1 + queueConfig.images.length) %
                                      queueConfig.images.length
                                  )
                                }
                                style={{
                                  position: "absolute",
                                  left: "8px",
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  background: "rgba(255, 255, 255, 0.7)",
                                  border: "none",
                                  borderRadius: "50%",
                                  width: "34px",
                                  height: "34px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: "pointer",
                                  fontSize: "17px",
                                  zIndex: 10,
                                  transition: "all 0.2s ease"
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.9)";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.7)";
                                }}
                              >
                                ❮
                              </button>
                              <button
                                onClick={() =>
                                  setCurrentImageIndex(
                                    (prevIndex) =>
                                      (prevIndex + 1) % queueConfig.images.length
                                  )
                                }
                                style={{
                                  position: "absolute",
                                  right: "8px",
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  background: "rgba(255, 255, 255, 0.7)",
                                  border: "none",
                                  borderRadius: "50%",
                                  width: "34px",
                                  height: "34px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: "pointer",
                                  fontSize: "17px",
                                  zIndex: 10,
                                  transition: "all 0.2s ease"
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.9)";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.7)";
                                }}
                              >
                                ❯
                              </button>
                            </>
                          )}
                        </div>
                      ) : (
                        // Fallback: Mostrar ícone Clock quando não houver imagens
                        <div style={{ position: "relative" }}>
                          <div style={{
                            position: "absolute",
                            inset: 0,
                            background: "linear-gradient(135deg, #4338ca 0%, #764ba2 100%)",
                            borderRadius: "24px",
                            filter: "blur(20px)",
                            opacity: 0.4
                          }} />
                          <div style={{
                            position: "relative",
                            padding: "16px",
                            borderRadius: "24px",
                            background: "linear-gradient(135deg, #4338ca 0%, #764ba2 100%)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                          }}>
                            <Clock size={40} color="white" strokeWidth={2.5} />
                          </div>
                        </div>
                      )}
                    </div>
                    <div>
                      <h1 style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: queueConfig && queueConfig.images && queueConfig.images.length > 0 ? "clamp(1.92rem, 4.8vw, 2.88rem)" : "clamp(2rem, 5vw, 3rem)",
                        fontWeight: "800",
                        color: "#2d3436",
                        marginBottom: "12px",
                        letterSpacing: "-0.5px"
                      }}>
                        <span style={{
                          background: "linear-gradient(135deg, #4338ca 0%, #764ba2 100%)",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          backgroundClip: "text"
                        }}>Fila de Espera</span>
                      </h1>
                      <p style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontWeight: "500",
                        color: "#636e72",
                        fontSize: "clamp(0.9rem, 2vw, 1.1rem)",
                        maxWidth: "500px",
                        margin: "0 auto"
                      }}>
                        Preencha os dados abaixo e aguarde ser chamado
                      </p>
                    </div>
                  </div>

                  {/* Card Dados da Fila */}
                  <div 
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-3px)";
                      e.currentTarget.style.boxShadow = "0 12px 32px rgba(99, 102, 241, 0.22), 0 8px 16px rgba(99, 102, 241, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.6)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 8px 24px rgba(99, 102, 241, 0.15), 0 4px 12px rgba(99, 102, 241, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.5)";
                    }}
                    style={{
                      background: "linear-gradient(135deg, rgba(99, 102, 241, 0.12) 0%, rgba(79, 70, 229, 0.08) 50%, rgba(67, 56, 202, 0.1) 100%)",
                      backdropFilter: "blur(20px)",
                      WebkitBackdropFilter: "blur(20px)",
                      borderRadius: "20px",
                      padding: "clamp(16px, 3vw, 32px)",
                      marginBottom: "clamp(20px, 3vw, 32px)",
                      boxShadow: "0 8px 24px rgba(99, 102, 241, 0.15), 0 4px 12px rgba(99, 102, 241, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.5)",
                      border: "1px solid rgba(99, 102, 241, 0.25)",
                      position: "relative" as const,
                      overflow: "hidden",
                      transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
                    }}
                  >
                    <div style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: "linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 100%)",
                      pointerEvents: "none"
                    }}></div>
                    <div style={{ position: "relative", zIndex: 1 }}>
                      <h3 style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontWeight: "700",
                        fontSize: "22px",
                        marginBottom: "24px",
                        color: "#4377FF",
                        textAlign: "center",
                        letterSpacing: "0.5px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "8px"
                      }}>
                        <Users size={20} color="#4377FF" />
                        Dados da Fila
                      </h3>

                      {/* Formulário de Entrada na Fila */}
                      <div>
                        {/* Nome completo */}
                        <div style={{ marginBottom: "24px" }}>
                          <label style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#512da8",
                            marginBottom: "8px",
                            fontFamily: "'Poppins', sans-serif"
                          }}>
                            <User size={18} style={{ color: "#4377FF" }} />
                            Nome completo
                          </label>
                          <input
                            type="text"
                            value={name}
                            onChange={(event) => {
                              const isValidName = /^[a-zA-ZÀ-ÿ\s]*$/.test(
                                event.target.value
                              );
                              if (isValidName) setName(event.target.value);
                            }}
                            placeholder="Digite seu nome completo"
                            style={{
                              width: "100%",
                              padding: "12px",
                              fontSize: "15px",
                              border: "2px solid #e0e0e0",
                              borderRadius: "12px",
                              outline: "none",
                              transition: "all 0.2s ease",
                              fontFamily: "'Poppins', sans-serif",
                              backgroundColor: "#fff",
                              boxSizing: "border-box",
                              height: "44px"
                            }}
                          />
                        </div>

                        {/* Celular + Número de pessoas + Fila preferencial */}
                        <div style={{
                      display: "flex",
                      gap: "16px",
                      marginBottom: "24px",
                      flexWrap: "wrap"
                    }}>
                      {/* Celular */}
                      <div style={{ flex: "1", minWidth: "180px" }}>
                        <label style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#512da8",
                          marginBottom: "8px",
                          fontFamily: "'Poppins', sans-serif"
                        }}>
                          <Phone size={18} style={{ color: "#4377FF" }} />
                          Celular
                        </label>
                        <InputMask
                          mask={maskTelephone}
                          type="text"
                          value={phone}
                          onBeforeInputCapture={(event) =>
                            changePhone(event.currentTarget.value)
                          }
                          onChange={(event) => setPhone(event.target.value)}
                          placeholder="(00) 00000-0000"
                          style={{
                            width: "100%",
                            padding: "12px",
                            fontSize: "15px",
                            border: "2px solid #e0e0e0",
                            borderRadius: "12px",
                            outline: "none",
                            transition: "all 0.2s ease",
                            fontFamily: "'Poppins', sans-serif",
                            backgroundColor: "#fff",
                            boxSizing: "border-box",
                            height: "44px"
                          }}
                        />
                      </div>

                      {/* Número de pessoas do grupo */}
                      <div style={{ flex: "1", minWidth: "180px" }}>
                        <label style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#512da8",
                          marginBottom: "8px",
                          fontFamily: "'Poppins', sans-serif"
                        }}>
                          <Users size={18} style={{ color: "#4377FF" }} />
                          Número de pessoas do grupo
                        </label>
                        <input
                          type="number"
                          value={people ? people : ""}
                          onChange={(event) => setPeople(parseInt(event.target.value))}
                          placeholder="0"
                          max={queueConfig.limitPeople}
                          min={0}
                          style={{
                            width: "100%",
                            padding: "12px",
                            fontSize: "15px",
                            border: "2px solid #e0e0e0",
                            borderRadius: "12px",
                            outline: "none",
                            transition: "all 0.2s ease",
                            fontFamily: "'Poppins', sans-serif",
                            backgroundColor: "#fff",
                            boxSizing: "border-box",
                            height: "44px"
                          }}
                        />
                      </div>

                      {/* Fila preferencial */}
                      <div style={{ flex: "1", minWidth: "180px" }}>
                        <label style={{
                          display: "block",
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#512da8",
                          marginBottom: "8px",
                          fontFamily: "'Poppins', sans-serif"
                        }}>
                          Fila preferencial
                        </label>
                        <select
                          onChange={(e) => setPreferentialQueue(e.target.value)}
                          value={preferentialQueue}
                          style={{
                            width: "100%",
                            padding: "0 12px",
                            fontSize: "15px",
                            border: "2px solid #e0e0e0",
                            borderRadius: "12px",
                            outline: "none",
                            transition: "all 0.2s ease",
                            fontFamily: "'Poppins', sans-serif",
                            backgroundColor: "#fff",
                            cursor: "pointer",
                            boxSizing: "border-box",
                            height: "44px",
                            lineHeight: "44px",
                            display: "block",
                            verticalAlign: "middle"
                          }}
                        >
                          <option value="y">Sim</option>
                          <option value="n">Não</option>
                        </select>
                      </div>
                    </div>

                    {/* Botão de entrada na fila */}
                    {acceptTerm || !queueConfig.useTerms ? (
                      <button
                        onClick={() => enterQueue()}
                        disabled={loaderLocation}
                        style={{
                          width: "100%",
                          padding: "16px",
                          fontSize: "16px",
                          fontWeight: "700",
                          color: "#fff",
                          background: loaderLocation ? "#9ca3af" : "linear-gradient(135deg, #4338ca 0%, #764ba2 100%)",
                          border: "none",
                          borderRadius: "12px",
                          cursor: loaderLocation ? "not-allowed" : "pointer",
                          fontFamily: "'Poppins', sans-serif",
                          boxShadow: "0 4px 12px rgba(102, 126, 234, 0.4)",
                          transition: "all 0.3s ease",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "8px"
                        }}
                      >
                        {loaderLocation ? "Entrando na fila..." : "Entrar na fila de espera →"}
                      </button>
                    ) : (
                      <button
                        onClick={() => setOpenTerms(true)}
                        style={{
                          width: "100%",
                          padding: "16px",
                          fontSize: "16px",
                          fontWeight: "700",
                          color: "#fff",
                          background: "linear-gradient(135deg, #4338ca 0%, #764ba2 100%)",
                          border: "none",
                          borderRadius: "12px",
                          cursor: "pointer",
                          fontFamily: "'Poppins', sans-serif",
                          boxShadow: "0 4px 12px rgba(102, 126, 234, 0.4)",
                          transition: "all 0.3s ease"
                        }}
                      >
                        Termos de Consentimento
                      </button>
                    )}
                      </div>
                    </div>
                  </div>

                  {/* Já está na fila? */}
                  <div style={{
                    background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
                    borderRadius: "16px",
                    padding: "clamp(16px, 3vw, 32px)",
                    marginTop: "clamp(20px, 3vw, 32px)"
                  }}>
                    <h3 style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: "20px",
                      fontWeight: "700",
                      color: "#1c1c1c",
                      marginBottom: "8px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px"
                    }}>
                      <Search size={24} style={{ color: "#4377FF" }} />
                      Já está na fila de espera?
                    </h3>
                    <p style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: "14px",
                      color: "#666",
                      marginBottom: "20px"
                    }}>
                      Informe o seu telefone abaixo para ver a sua posição
                    </p>
                    <div style={{ marginBottom: "16px" }}>
                      <label style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#512da8",
                        marginBottom: "8px",
                        fontFamily: "'Poppins', sans-serif"
                      }}>
                        <Phone size={18} style={{ color: "#4377FF" }} />
                        Celular
                      </label>
                      <InputMask
                        mask={maskTelephoneFind}
                        type="text"
                        value={phoneFind}
                        onBeforeInputCapture={(event) =>
                          changePhoneFind(event.currentTarget.value)
                        }
                        onChange={(event) => setPhoneFind(event.target.value)}
                        placeholder="(00) 00000-0000"
                        style={{
                          width: "100%",
                          padding: "12px",
                          fontSize: "15px",
                          border: "2px solid #e0e0e0",
                          borderRadius: "12px",
                          outline: "none",
                          transition: "all 0.2s ease",
                          fontFamily: "'Poppins', sans-serif",
                          backgroundColor: "#fff",
                          boxSizing: "border-box",
                          height: "44px"
                        }}
                      />
                    </div>
                    <button
                      onClick={() =>
                        navigate(
                          `/${pluginspace_id}/queue/${telephoneNoMask(phoneFind)}`
                        )
                      }
                      style={{
                        width: "100%",
                        padding: "14px",
                        fontSize: "15px",
                        fontWeight: "600",
                        color: "#4377FF",
                        background: "#fff",
                        border: "2px solid #4377FF",
                        borderRadius: "12px",
                        cursor: "pointer",
                        fontFamily: "'Poppins', sans-serif",
                        transition: "all 0.3s ease"
                      }}
                    >
                      Procurar
                    </button>
                  </div>
              </div>
            )}
            {openTerms && (
              <ModalTerms
                isOpen={openTerms}
                setIsOpen={setOpenTerms}
                termContent={term}
                backgroundButton={
                  config?.webappcolor.webappbutton &&
                  config.webappcolor.webappbutton
                }
                fontButton={
                  config?.webappcolor.fontbutton && config.webappcolor.fontbutton
                }
                acceptTerm={acceptTerm}
                setAcceptTerm={setAcceptTerm}
              />
            )}
          </div>
        )}
      </div>
    </>
  );
}
